/**
 * Development Bridge Initializer
 *
 * This module handles conditional initialization of the dummy native bridge
 * for development/testing scenarios. In production, the real native bridge
 * is injected by the host application.
 */

/**
 * Initialize the appropriate bridge based on the environment
 */
export async function initializeBridge(): Promise<void> {
	// Check if real native bridge is available
	if (
		typeof window !== 'undefined' &&
		window.settingsNative &&
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		!(window.settingsNative as any)._isDummy
	) {
		console.log('Real native bridge detected');
		return;
	}

	// Initialize dummy bridge for development
	try {
		await initializeDummyBridge();
	} catch (error) {
		console.error('Bridge initialization failed:', error);
		// Don't throw - let the app continue and the bridge factory will handle fallbacks
	}
}

/**
 * Dynamically loads and initializes the dummy bridge when needed
 */
async function initializeDummyBridge(): Promise<void> {
	console.log('Loading dummy bridge for development...');

	try {
		// Load dummy bridge from webpack output (copied by CopyDummyBridgePlugin)
		const script = document.createElement('script');
		script.src = './DummyNativeBridge.js';
		script.async = false;

		return new Promise<void>((resolve, reject) => {
			script.onload = () => {
				// Give the bridge a moment to initialize
				setTimeout(() => {
					if (typeof window !== 'undefined' && window.settingsNative) {
						console.log('Bridge ready');
						resolve();
					} else {
						console.error(
							'Dummy native bridge script loaded but window.settingsNative not available'
						);
						reject(new Error('Dummy bridge not properly initialized'));
					}
				}, 100);
			};
			script.onerror = () => {
				console.error(
					'Failed to load DummyNativeBridge.js - make sure CopyDummyBridgePlugin is enabled'
				);
				reject(new Error('Dummy bridge not available'));
			};

			document.head.appendChild(script);
		});
	} catch (error) {
		console.error('Dummy native bridge initialization failed:', error);
		throw error;
	}
}

/**
 * Check if any native bridge (real or dummy) is available
 */
export function isBridgeAvailable(): boolean {
	return typeof window !== 'undefined' && typeof window.settingsNative !== 'undefined';
}
